import React, { useMemo } from 'react';
import { Pressable, View, Text, StyleSheet } from 'react-native';
import { useTheme, makeThemedStyles, DISPLAY_FONT } from '../../v10/theme';
import { GlassCard } from '../../v10/ui';
import type { OrderWidgetProps } from './types';
import {
  dateKey,
  startOfWeekSun,
  addDays,
  isSameDate,
  isEventOccurringOnDate,
  renderRange,
  computeOverlapLayout,
  resolveColorHex,
  parseTimeToMinutes,
} from '../../tools/KrnosTool';
import type { EventRow } from '../../tools/KrnosTool';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Compact rendering: 8 pixels per 30-min interval = 16px per hour
const COMPACT_SLOT_HEIGHT = 8;
const COMPACT_INTERVAL_MINUTES = 30;
const DAY_COLUMN_WIDTH = 42;

// Compact time window: 7am-8pm (13 hours = 26 slots at 30min interval)
const COMPACT_DAY_START = '07:00';
const COMPACT_DAY_END = '20:00';

export function KrnosWidget({ userId, gear, data, onOpen }: OrderWidgetProps) {
  useTheme();
  const styles = useStyles();
  const today = startOfWeekSun(data.now);

  // This week's 7 days
  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => addDays(today, i));
  }, [today]);

  const timeSlots = useMemo(() => {
    const start = parseTimeToMinutes(COMPACT_DAY_START);
    const end = parseTimeToMinutes(COMPACT_DAY_END);
    const slots: string[] = [];
    for (let cur = start; cur < end; cur += COMPACT_INTERVAL_MINUTES) {
      const h = Math.floor(cur / 60);
      const m = cur % 60;
      slots.push(`${h < 10 ? '0' : ''}${h}:${m < 10 ? '0' : ''}${m}`);
    }
    return slots;
  }, []);

  const dayStartMin = parseTimeToMinutes(COMPACT_DAY_START);
  const dayEndMin = parseTimeToMinutes(COMPACT_DAY_END);
  const pxPerMinute = COMPACT_SLOT_HEIGHT / COMPACT_INTERVAL_MINUTES;
  const gridHeight = timeSlots.length * COMPACT_SLOT_HEIGHT;

  // Build events by day
  const eventsByDay = useMemo(() => {
    const map = new Map<string, EventRow[]>();
    const events = (data.calendarEvents ?? []) as EventRow[];
    for (const day of weekDays) {
      const key = dateKey(day);
      const dayEvents = events.filter((ev) => isEventOccurringOnDate(ev, day));
      map.set(key, dayEvents);
    }
    return map;
  }, [weekDays, data.calendarEvents]);

  // Check if week is empty
  const hasEvents = useMemo(() => {
    for (const events of eventsByDay.values()) {
      if (events.length > 0) return true;
    }
    return false;
  }, [eventsByDay]);

  if (!hasEvents) {
    return (
      <Pressable onPress={onOpen}>
        <GlassCard style={styles.container}>
          <View style={styles.emptyState}>
            <Text style={styles.emptyText}>No events scheduled this week</Text>
          </View>
        </GlassCard>
      </Pressable>
    );
  }

  return (
    <Pressable onPress={onOpen}>
    <GlassCard style={styles.container}>
      {/* Week day header row */}
      <View style={styles.weekHeader}>
        {weekDays.map((day) => {
          const isToday = isSameDate(day, data.now);
          return (
            <View
              key={dateKey(day)}
              style={[
                styles.dayHeader,
                isToday && styles.dayHeaderToday,
              ]}
            >
              <Text style={[styles.dayHeaderName, isToday && styles.dayHeaderNameToday]}>
                {DAY_NAMES[day.getDay()]}
              </Text>
              <Text style={[styles.dayHeaderDate, isToday && styles.dayHeaderDateToday]}>
                {day.getDate()}
              </Text>
            </View>
          );
        })}
      </View>

      {/* Compact grid */}
      <View style={[styles.gridRow, { height: gridHeight }]}>
        {weekDays.map((day) => {
          const dayKey = dateKey(day);
          const dayEvents = eventsByDay.get(dayKey) ?? [];
          const overlap = computeOverlapLayout(dayEvents, COMPACT_INTERVAL_MINUTES);
          const isToday = isSameDate(day, data.now);

          return (
            <View
              key={dayKey}
              style={[
                styles.dayColumn,
                { width: DAY_COLUMN_WIDTH, height: gridHeight },
                isToday && styles.dayColumnToday,
              ]}
            >
              {/* Event blocks */}
              {dayEvents.map((item) => {
                const { start, end } = renderRange(item, COMPACT_INTERVAL_MINUTES);
                const clampedStart = Math.max(start, dayStartMin);
                const clampedEnd = Math.min(end, dayEndMin);
                if (clampedEnd <= clampedStart) return null;

                const top = (clampedStart - dayStartMin) * pxPerMinute;
                const height = Math.max((clampedEnd - clampedStart) * pxPerMinute - 1, 4);
                const layout = overlap.get(item.id);
                const totalCols = layout?.totalColumns ?? 1;
                const colIndex = layout?.columnIndex ?? 0;
                const widthPct = 100 / totalCols;
                const leftPct = colIndex * widthPct;
                const bg = resolveColorHex(item.color);
                const recurring = !!item.repeat && item.repeat !== 'none';

                return (
                  <View
                    key={item.id}
                    style={[
                      styles.eventBlock,
                      {
                        top,
                        height,
                        left: `${leftPct}%` as unknown as number,
                        width: `${widthPct}%` as unknown as number,
                        backgroundColor: bg + '38',
                        borderLeftColor: bg,
                      },
                    ]}
                  >
                    {height > 12 && (
                      <Text style={styles.eventTitle} numberOfLines={1}>
                        {recurring ? '⟳ ' : ''}{item.title}
                      </Text>
                    )}
                  </View>
                );
              })}
            </View>
          );
        })}
      </View>
    </GlassCard>
    </Pressable>
  );
}

const useStyles = makeThemedStyles(({ t, c }) =>
  StyleSheet.create({
  container: {
    borderRadius: 22,
    paddingVertical: 8,
    paddingHorizontal: 8,
    maxHeight: 320,
  },
  emptyState: {
    height: 160,
    backgroundColor: c.limeSoft,
    borderRadius: 6,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    fontSize: 13,
    color: c.textMuted,
  },
  weekHeader: {
    flexDirection: 'row',
    marginBottom: 4,
    gap: 1,
  },
  dayHeader: {
    flex: 1,
    alignItems: 'center',
    paddingVertical: 4,
    backgroundColor: c.limeSoft,
    borderRadius: 4,
  },
  dayHeaderToday: {
    backgroundColor: c.lime,
  },
  dayHeaderName: {
    fontSize: 9,
    fontWeight: '900',
    color: t.ivorySoft,
    lineHeight: 11,
  },
  dayHeaderNameToday: {
    color: c.blueText,
  },
  dayHeaderDate: {
    fontSize: 8,
    color: t.ivorySoft,
    lineHeight: 10,
  },
  dayHeaderDateToday: {
    color: c.blueText,
    fontWeight: '700',
  },
  gridRow: {
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: t.lineFaint,
    borderRadius: 4,
    overflow: 'hidden',
    gap: 1,
  },
  dayColumn: {
    position: 'relative',
  },
  dayColumnToday: {
    backgroundColor: c.lime,
  },
  eventBlock: {
    position: 'absolute',
    borderRadius: 3,
    borderLeftWidth: 2,
    paddingHorizontal: 2,
    paddingVertical: 1,
    zIndex: 5,
  },
  eventTitle: {
    color: t.ivory,
    fontSize: 6,
    fontWeight: '600',
    lineHeight: 8,
    fontFamily: DISPLAY_FONT,
    textTransform: 'uppercase',
  },
  }),
);
